import type { ShippingRule, ShippingMethod, CheckProductShippabilityInput } from '@/lib/types/shipping';
import { DEFAULT_COUNTRY_CODE, isUnitedStates } from '@/lib/constants/countries';
import { normalizeStateCode } from '@/lib/functions/normalizeStateCode';
import { normalizeZipCode } from '@/lib/functions/normalizeZipCode';
import { checkTypeMatch } from '@/lib/functions/typeMatching';

const debug = false;

export function evaluateRule(rule: ShippingRule, input: CheckProductShippabilityInput): boolean {
  const { type, configuration } = rule;
  const { category, classId, productType, provinceCode, postalCode, countryCode } = input;

  const country = (countryCode ?? DEFAULT_COUNTRY_CODE).toUpperCase();
  const state = normalizeStateCode(provinceCode);
  const zip = normalizeZipCode(postalCode);

  if (debug) {
    console.log(`[ShippingRules] Evaluating rule ${type}:`, {
      input: { category, classId, productType, provinceCode, postalCode },
      normalized: { state, zip },
      configuration,
    });
  }

  switch (type) {
    case 'contains_types': {
      const action = configuration.action || 'allow';
      const types = (configuration.types || '').split(',').map((t: string) => t.trim()).filter(Boolean);

      if (types.length === 0) {
        return true;
      }

      const typeMatches = checkTypeMatch(productType || '', types);
      return action === 'allow' ? typeMatches : !typeMatches;
    }

    case 'contains_class_codes': {
      const action = configuration.action || 'require';
      const codes = (configuration.codes || '').split(',').map((c: string) => c.trim()).filter(Boolean);

      if (codes.length === 0) {
        return true;
      }

      const classIdMatches = classId && codes.includes(String(classId).trim());
      return action === 'require' ? Boolean(classIdMatches) : !classIdMatches;
    }

    case 'restricted_product_zip': {
      const restrictedTypes = (configuration.types || '').split(',').map((t: string) => t.trim()).filter(Boolean);
      const restrictedClassCodes = (configuration.class_codes || '').split(',').map((c: string) => c.trim()).filter(Boolean);
      const allowedZips = (configuration.allowed_zips || '').split(',').map((z: string) => z.trim()).filter(Boolean);

      if (restrictedTypes.length === 0 && restrictedClassCodes.length === 0) {
        return true;
      }

      const productTypeMatches = productType && restrictedTypes.includes(productType);
      const classCodeMatches = classId && restrictedClassCodes.includes(String(classId).trim());

      if (!productTypeMatches && !classCodeMatches) {
        return true;
      }

      return zip !== null && allowedZips.includes(zip);
    }

    case 'restricted_product_state': {
      const restrictedTypes = (configuration.types || '').split(',').map((t: string) => t.trim()).filter(Boolean);
      const restrictedClassCodes = (configuration.class_codes || '').split(',').map((c: string) => c.trim()).filter(Boolean);
      const allowedStates = (configuration.allowed_states || '').split(',').map((s: string) => s.trim()).filter(Boolean);

      if (restrictedTypes.length === 0 && restrictedClassCodes.length === 0) {
        return true;
      }

      const productTypeMatches = productType && restrictedTypes.includes(productType);
      const classCodeMatches = classId && restrictedClassCodes.includes(String(classId).trim());

      if (!productTypeMatches && !classCodeMatches) {
        return true;
      }

      return state !== null && allowedStates.includes(state);
    }

    case 'exclude_shipping_to_states': {
      const excludedStates = (configuration.excluded_states || '').split(',').map((s: string) => s.trim()).filter(Boolean);

      if (excludedStates.length === 0) {
        return true;
      }

      return state === null || !excludedStates.includes(state);
    }

    case 'exclude_shipping_outside_of_texas': {
      return state === null || state === 'TX';
    }

    case 'exclude_shipping_outside_of_us': {
      return isUnitedStates(country);
    }

    case 'exclude_shipping_to_us': {
      return !isUnitedStates(country);
    }

    case 'items_total_greater_than_or_equal':
    case 'items_total_less_than_or_equal':
    case 'total_weight_greater_than_or_equal':
    case 'total_weight_less_than_or_equal':
      return true;

    default:
      return true;
  }
}

export function isShippingMethodEligible(method: ShippingMethod, input: CheckProductShippabilityInput): boolean {
  return method.rules.every((rule) => evaluateRule(rule, input));
}
